import React, { useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { Search, SlidersHorizontal, X, LayoutGrid, List, Check } from "lucide-react";
import { getProducts } from "@/lib/api";
import { useSEO, breadcrumbSchema } from "@/lib/seo";
import PageHeader from "@/components/PageHeader";
import ProductCard from "@/components/shop/ProductCard";
import ProductListRow from "@/components/shop/ProductListRow";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const SORTS = [
  { value: "featured", label: "Featured" },
  { value: "name-asc", label: "Name: A–Z" },
  { value: "name-desc", label: "Name: Z–A" },
];

const AVAIL = [
  { value: "all", label: "All" },
  { value: "in-stock", label: "In Stock" },
];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [drawer, setDrawer] = useState(false);
  const [view, setView] = useState("grid");

  const collection = params.get("collection") || "all";
  const sort = params.get("sort") || "featured";
  const availability = params.get("availability") || "all";
  const q = params.get("q") || "";

  const setParam = useCallback((key, value) => {
    const next = new URLSearchParams(params);
    if (!value || value === "all" || value === "") next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  }, [params, setParams]);

  const { data, isLoading } = useQuery({
    queryKey: ["products", collection, sort, availability, q],
    queryFn: () => getProducts({ collection, sort, availability, q }),
  });

  const products = data?.products || [];
  const count = data?.count ?? 0;

  useSEO({
    title: "Shop Research Products | Renova Peptide Co.",
    description:
      "Browse Renova Peptide Co. research materials. Search, filter, and view details, with secure checkout handled through our connected store.",
    path: "/shop",
    jsonLd: breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Shop", path: "/shop" },
    ]),
  });

  const Filters = useMemo(
    () => (
      <div className="space-y-10">
        <div>
          <h3 className="overline mb-4">Availability</h3>
          <ul className="space-y-1">
            {AVAIL.map((a) => (
              <FilterButton
                key={a.value}
                active={availability === a.value}
                onClick={() => setParam("availability", a.value)}
                label={a.label}
                testid={`filter-avail-${a.value}`}
              />
            ))}
          </ul>
        </div>
        <p className="text-xs text-[#85827D] leading-relaxed border-t border-[#1E2A44]/10 pt-6">
          Live pricing, inventory, variants, and checkout are handled by our connected store.
        </p>
      </div>
    ),
    [availability, setParam]
  );

  return (
    <>
      <PageHeader
        overline="Catalogue"
        title="Shop Research Products"
        intro="Carefully catalogued research materials. All items are for laboratory research use only."
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Shop", path: "/shop" }]}
      />

      <section className="container-rnv py-12 md:py-16">
        {/* Search + controls */}
        <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between mb-10">
          <div className="relative flex-1 max-w-md">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#85827D]" />
            <input
              type="search"
              value={q}
              onChange={(e) => setParam("q", e.target.value)}
              placeholder="Search research materials…"
              aria-label="Search products"
              className="w-full bg-white border border-[#1E2A44]/12 rounded-sm pl-11 pr-4 py-3 text-sm focus:outline-none focus:border-[#C8A96A]"
              data-testid="shop-search-input"
            />
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setDrawer(true)} className="lg:hidden btn-gold !py-2.5 !px-4 text-sm" data-testid="shop-open-filters">
              <SlidersHorizontal size={16} /> Filters
            </button>
            <div className="hidden sm:flex items-center border border-[#1E2A44]/12 rounded-sm overflow-hidden">
              <button onClick={() => setView("grid")} aria-label="Grid view" className={`p-2.5 ${view === "grid" ? "bg-[#1E2A44] text-white" : "text-[#85827D]"}`} data-testid="view-grid"><LayoutGrid size={16} /></button>
              <button onClick={() => setView("list")} aria-label="List view" className={`p-2.5 ${view === "list" ? "bg-[#1E2A44] text-white" : "text-[#85827D]"}`} data-testid="view-list"><List size={16} /></button>
            </div>
            <Select value={sort} onValueChange={(v) => setParam("sort", v)}>
              <SelectTrigger className="w-[160px] rounded-sm border-[#1E2A44]/12" data-testid="shop-sort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SORTS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <aside className="hidden lg:block lg:col-span-3">{Filters}</aside>

          <div className="lg:col-span-9">
            <p className="text-sm text-[#85827D] mb-6" data-testid="product-count">
              {isLoading ? "Loading…" : `${count} ${count === 1 ? "product" : "products"}`}
            </p>

            {!isLoading && products.length === 0 ? (
              <div className="text-center py-24 border border-dashed border-[#1E2A44]/15 rounded-sm" data-testid="shop-empty">
                <p className="font-serif text-2xl text-[#1E2A44]">No products found</p>
                <p className="mt-2 text-sm text-[#85827D]">Try adjusting your search or filters.</p>
                <button onClick={() => setParams({})} className="btn-gold mt-6">Clear filters</button>
              </div>
            ) : view === "grid" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="product-grid">
                {products.map((p) => <ProductCard key={p.slug} product={p} />)}
              </div>
            ) : (
              <div className="flex flex-col divide-y divide-[#1E2A44]/10 border-t border-[#1E2A44]/10" data-testid="product-list">
                {products.map((p) => <ProductListRow key={p.slug} product={p} />)}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Mobile filter drawer */}
      <AnimatePresence>
        {drawer && (
          <>
            <motion.div className="fixed inset-0 bg-[#111C32]/50 z-[80] lg:hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setDrawer(false)} />
            <motion.div
              className="fixed right-0 top-0 bottom-0 w-[85%] max-w-sm bg-[#F7F4EF] z-[81] lg:hidden p-8 overflow-y-auto"
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ ease: [0.22, 1, 0.36, 1], duration: 0.4 }}
              data-testid="shop-filter-drawer"
            >
              <div className="flex items-center justify-between mb-8">
                <h2 className="font-serif text-2xl text-[#1E2A44]">Filters</h2>
                <button onClick={() => setDrawer(false)} aria-label="Close filters" data-testid="shop-close-filters"><X size={24} className="text-[#1E2A44]" /></button>
              </div>
              {Filters}
              <button onClick={() => setDrawer(false)} className="btn-navy w-full mt-10">Show {count} results</button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

function FilterButton({ active, onClick, label, testid }) {
  return (
    <li>
      <button
        onClick={onClick}
        className={`w-full flex items-center justify-between text-left text-sm py-2 transition-colors ${
          active ? "text-[#1E2A44] font-semibold" : "text-[#85827D] hover:text-[#1E2A44]"
        }`}
        data-testid={testid}
      >
        {label}
        {active && <Check size={14} className="text-[#C8A96A]" />}
      </button>
    </li>
  );
}
